import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  List,
  Avatar,
  Skeleton,
  Tooltip,
  Grid,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CopyOutlined,
  ReloadOutlined,
  LikeOutlined,
  CommentOutlined,
  ShareAltOutlined,
  FileTextOutlined,
  PictureOutlined,
  LinkOutlined,
  VideoCameraOutlined,
  GlobalOutlined,
  IdcardOutlined,
  FacebookFilled,
} from "@ant-design/icons";
import config from "../../config";
import { postsService } from "../../services/posts.service";
import { pagesService } from "../../services/pages.service";
import { autoPostService } from "../../services/autopost.service";
import { Post, Page, AutoPostRule, PostType } from "../../types";
import PageSelect from "../../components/Shared/PageSelect";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const PageDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState<Page | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statsLoading, setStatsLoading] = useState<string | null>(null);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Copy Post Modal State
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [targetPages, setTargetPages] = useState<Page[]>([]);
  const [autoPostRules, setAutoPostRules] = useState<AutoPostRule[]>([]);
  const [copyForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      fetchPageDetails();
      fetchPosts();
      fetchAllPages(); // For copy functionality
      fetchAutoPostRules();
    }
  }, [id]);

  const fetchPageDetails = async () => {
    try {
      const data = await pagesService.getById(id!);
      setPage(data);
    } catch (error) {
      message.error("Không thể tải chi tiết trang");
      navigate("/pages");
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await postsService.getByPage(id!);
      setPosts(data);
    } catch (error) {
      console.error(error);
      message.error("Không thể tải bài viết");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPages = async () => {
    try {
      const data = await pagesService.getAll();
      setTargetPages(data.filter((p) => p.id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAutoPostRules = async () => {
    try {
      const rules = await autoPostService.getAll();
      setAutoPostRules(rules.filter((r) => r.isActive));
    } catch (error) {
      console.error(error);
    }
  };

  const selectedTargetIds = Form.useWatch("targetPageIds", copyForm) || [];

  const isPageAutoPostTarget = (targetId: string) => {
    return autoPostRules.some(
      (r) =>
        r.sourcePageId === id &&
        (Array.isArray(r.targetPageIds)
          ? r.targetPageIds.includes(targetId)
          : r.targetPageIds === targetId),
    );
  };

  const isPageAutoPostSource = (targetId: string) => {
    return autoPostRules.some(
      (r) =>
        r.sourcePageId === targetId &&
        (Array.isArray(r.targetPageIds)
          ? r.targetPageIds.includes(id!)
          : r.targetPageIds === id),
    );
  };

  const checkInterTargetConflict = (pageId: string) => {
    for (const sId of selectedTargetIds) {
      if (sId === pageId) continue;

      const pIdPushesToSId = autoPostRules.some(
        (r) =>
          r.sourcePageId === pageId &&
          (Array.isArray(r.targetPageIds)
            ? r.targetPageIds.includes(sId)
            : r.targetPageIds === sId),
      );
      if (pIdPushesToSId)
        return `Trang này đang đẩy bài tự động sang trang "${targetPages.find((p) => p.id === sId)?.name}" đã chọn`;

      const sIdPushesToPId = autoPostRules.some(
        (r) =>
          r.sourcePageId === sId &&
          (Array.isArray(r.targetPageIds)
            ? r.targetPageIds.includes(pageId)
            : r.targetPageIds === pageId),
      );
      if (sIdPushesToPId)
        return `Trang "${targetPages.find((p) => p.id === sId)?.name}" đã chọn đang đẩy bài tự động sang trang này`;
    }
    return null;
  };

  const handleSyncPosts = async () => {
    setSyncing(true);
    try {
      await postsService.sync(id!);
      message.success("Đồng bộ bài viết thành công");
      fetchPosts();
    } catch (error) {
      message.error("Đồng bộ bài viết thất bại");
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateStats = async (postId: string) => {
    setStatsLoading(postId);
    try {
      const stats = await postsService.getStats(postId);
      setPosts((current) =>
        current.map((p) => (p.id === postId ? { ...p, stats } : p)),
      );
      message.success("Đã cập nhật chỉ số");
    } catch (error) {
      message.error("Cập nhật chỉ số thất bại");
    } finally {
      setStatsLoading(null);
    }
  };

  const handleCopyPost = async (values: any) => {
    if (!selectedPost) return;

    setCopyLoading(true);
    try {
      const result = await postsService.copy(
        selectedPost.id,
        values.targetPageIds,
      );
      message.success(
        result.message || "Đã thêm yêu cầu sao chép vào hàng đợi",
      );
      setIsCopyModalVisible(false);
      copyForm.resetFields();
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        message.error(error.response.data.message);
      } else {
        message.error("Sao chép bài viết thất bại");
      }
    } finally {
      setCopyLoading(false);
    }
  };

  const handleCopyAndEdit = async () => {
    if (!selectedPost) return;

    try {
      const values = await copyForm.validateFields();

      setCopyLoading(true);
      // Fetch fresh details from FB to get complete media (e.g. albums)
      const fbDetails = await postsService.getFacebookDetails(selectedPost.id);

      navigate("/posts/create", {
        state: {
          message: fbDetails.message || selectedPost.content.message,
          type:
            fbDetails.type === "album"
              ? PostType.PHOTO
              : (fbDetails.type as any),
          mediaUrl: fbDetails.videoUrl || fbDetails.full_picture,
          mediaUrls: fbDetails.mediaUrls,
          link: fbDetails.link,
          pageIds: values.targetPageIds,
        },
      });
    } catch (err) {
      console.log("Validation failed or Fetch failed:", err);
      if ((err as any).response?.data?.message) {
        message.error((err as any).response.data.message);
      }
    } finally {
      setCopyLoading(false);
    }
  };

  const openCopyModal = (post: Post) => {
    setSelectedPost(post);
    setIsCopyModalVisible(true);
  };

  const getPostIcon = (type: string) => {
    switch (type) {
      case "photo":
      case "album":
        return <PictureOutlined style={{ color: "#eb2f96" }} />;
      case "video":
        return <VideoCameraOutlined style={{ color: "#722ed1" }} />;
      case "link":
      case "share":
        return <LinkOutlined style={{ color: "#1890ff" }} />;
      case "status":
      case "text":
        return <FileTextOutlined style={{ color: "#52c41a" }} />;
      default:
        return <FileTextOutlined style={{ color: "#52c41a" }} />;
    }
  };

  const getPostTypeText = (type: string) => {
    switch (type) {
      case "photo":
        return "Hình ảnh";
      case "video":
        return "Video";
      case "link":
      case "share":
        return "Liên kết";
      case "status":
      case "text":
        return "Dòng trạng thái";
      case "album":
        return "Album ảnh";
      default:
        return type || "Văn bản";
    }
  };

  const getMediaUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/uploads/")) return `${config.BASE_URL}${url}`;
    if (!url.startsWith("http") && !url.startsWith("data:"))
      return `${config.BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
    return url;
  };

  return (
    <Space
      direction="vertical"
      size={isMobile ? "middle" : "large"}
      style={{ width: "100%" }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          background: "#fff",
          padding: isMobile ? "12px" : "16px 24px",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          gap: 16,
        }}
      >
        <Space size={isMobile ? "small" : "middle"}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/pages")}
            shape="circle"
            size={isMobile ? "small" : "middle"}
          />
          <Avatar
            src={page?.pictureUrl}
            size={isMobile ? 40 : 48}
            shape="square"
          />
          <div>
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              {page?.name || "Đang tải..."}
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              {page?.category}
            </Text>
          </div>
        </Space>
        <Space
          direction={isMobile ? "vertical" : "horizontal"}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          <Button
            icon={<FacebookFilled />}
            block={isMobile}
            size={isMobile ? "small" : "middle"}
            onClick={() =>
              window.open(`https://facebook.com/${page?.pageId}`, "_blank")
            }
          >
            {isMobile ? "Facebook" : "Truy cập Facebook"}
          </Button>
          <Button
            icon={<ReloadOutlined spin={syncing} />}
            onClick={handleSyncPosts}
            loading={syncing}
            block={isMobile}
            size={isMobile ? "small" : "middle"}
          >
            Đồng bộ bài viết
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block={isMobile}
            size={isMobile ? "small" : "middle"}
            onClick={() =>
              navigate("/posts/create", { state: { pageIds: [id] } })
            }
          >
            {isMobile ? "Tạo bài" : "Tạo bài viết mới"}
          </Button>
        </Space>
      </div>

      {/* Stats Summary */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card
            bordered={false}
            bodyStyle={{ padding: isMobile ? "12px" : "20px" }}
            style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          >
            <Statistic
              title="Tổng bài viết"
              value={posts.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#1890ff", fontSize: isMobile ? 20 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card
            bordered={false}
            bodyStyle={{ padding: isMobile ? "12px" : "20px" }}
            style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          >
            <Statistic
              title="Tổng lượt thích trang"
              value={page?.likes || 0}
              prefix={<LikeOutlined />}
              valueStyle={{ color: "#eb2f96", fontSize: isMobile ? 20 : 24 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Posts List */}
      <Card
        title={
          <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
            Danh sách bài viết đã đăng
          </Text>
        }
        bordered={false}
        bodyStyle={{ padding: isMobile ? "0 12px" : "0 24px" }}
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
      >
        <List
          loading={loading}
          itemLayout="vertical"
          size="large"
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
            size: isMobile ? "small" : (undefined as any),
          }}
          dataSource={posts}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              actions={[
                <Space
                  style={{ cursor: "pointer", fontSize: isMobile ? 12 : 14 }}
                  onClick={() => handleUpdateStats(item.id)}
                >
                  <LikeOutlined /> {item.stats?.likes || 0}
                  {statsLoading === item.id && <ReloadOutlined spin />}
                </Space>,
                <Space style={{ fontSize: isMobile ? 12 : 14 }}>
                  <CommentOutlined /> {item.stats?.comments || 0}
                </Space>,
                <Space style={{ fontSize: isMobile ? 12 : 14 }}>
                  <ShareAltOutlined /> {item.stats?.shares || 0}
                </Space>,
                item.status !== "pending" && (
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    size={isMobile ? "small" : "middle"}
                    style={{ padding: isMobile ? "0 4px" : "4px 15px" }}
                    onClick={() => openCopyModal(item)}
                  >
                    {isMobile ? "Sao chép" : "Sao chép bài viết"}
                  </Button>
                ),
              ]}
              extra={
                item.content.mediaUrl && (
                  <img
                    width={isMobile ? "100%" : 200}
                    height={isMobile ? 180 : 150}
                    alt="media"
                    src={getMediaUrl(item.content.mediaUrl)}
                    style={{
                      objectFit: "cover",
                      borderRadius: 8,
                      marginTop: isMobile ? 12 : 0,
                    }}
                  />
                )
              }
            >
              <List.Item.Meta
                avatar={!isMobile && getPostIcon(item.content.type)}
                title={
                  <Space
                    direction="vertical"
                    size={0}
                    style={{ width: "100%" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      {isMobile && getPostIcon(item.content.type)}
                      <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>
                        {getPostTypeText(item.content.type)}
                      </Text>
                      <Text
                        type="secondary"
                        style={{ fontSize: isMobile ? 10 : 12 }}
                      >
                        {new Date(item.publishedAt).toLocaleString("vi-VN")}
                      </Text>
                      <Tag
                        color={item.source === "synced" ? "blue" : "purple"}
                        style={{ fontSize: 9, margin: 0 }}
                      >
                        {item.source === "synced" ? "Facebook" : "App"}
                      </Tag>
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <Text
                        copyable={{ text: item.postId }}
                        type="secondary"
                        style={{ fontSize: 11 }}
                      >
                        <IdcardOutlined /> ID: {item.postId}
                      </Text>
                      {item.permalinkUrl && (
                        <a
                          href={item.permalinkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11 }}
                        >
                          <GlobalOutlined /> Xem trên Facebook
                        </a>
                      )}
                    </div>
                  </Space>
                }
                description={
                  <div
                    style={{
                      marginTop: 8,
                      whiteSpace: "pre-wrap",
                      color: "#262626",
                      fontSize: isMobile ? 13 : 15,
                    }}
                  >
                    {item.content.message || (
                      <Text type="secondary" italic>
                        Không có nội dung văn bản
                      </Text>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <Text type="secondary">Chưa có bài viết nào được đồng bộ.</Text>
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleSyncPosts}
                  >
                    Đồng bộ ngay
                  </Button>
                </div>
              </div>
            ),
          }}
        />
      </Card>

      {/* Copy Post Modal */}
      <Modal
        title="Sao chép bài viết sang trang khác"
        open={isCopyModalVisible}
        onCancel={() => setIsCopyModalVisible(false)}
        footer={null}
        width={isMobile ? "95%" : 500}
      >
        <Form form={copyForm} layout="vertical" onFinish={handleCopyPost}>
          <div
            style={{
              marginBottom: 16,
              padding: "8px 12px",
              background: "#f5f5f5",
              borderRadius: 4,
            }}
          >
            <Text strong style={{ fontSize: 13 }}>
              Bài viết nguồn:
            </Text>
            <div
              style={{
                marginTop: 8,
                maxHeight: 100,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Text style={{ fontSize: 13 }}>
                {selectedPost?.content?.message}
              </Text>
            </div>
            {selectedPost?.content?.mediaUrl && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={getMediaUrl(selectedPost.content.mediaUrl)}
                  alt="preview"
                  style={{ height: 60, objectFit: "cover", borderRadius: 4 }}
                />
              </div>
            )}
          </div>

          <Form.Item
            name="targetPageIds"
            label="Chọn các trang đích"
            rules={[
              {
                required: true,
                message: "Vui lòng chọn ít nhất một trang đích",
              },
            ]}
          >
            <PageSelect
              mode="multiple"
              pages={targetPages}
              placeholder="Chọn các trang để đăng bài"
              isPageDisabled={(pId) => {
                const targetIds = copyForm.getFieldValue("targetPageIds") || [];
                if (targetIds.includes(pId)) return false;

                const isTarget = isPageAutoPostTarget(pId);
                const isSource = isPageAutoPostSource(pId);
                const interConflict = checkInterTargetConflict(pId);

                if (isTarget) {
                  return {
                    disabled: true,
                    message:
                      "Trang này đã được cấu hình tự động nhận bài từ trang hiện tại",
                  };
                }
                if (isSource) {
                  return {
                    disabled: true,
                    message:
                      "Trang hiện tại đang nhận bài tự động từ trang này (tránh vòng lặp)",
                  };
                }
                if (interConflict) {
                  return { disabled: true, message: interConflict };
                }
                return false;
              }}
              allowClear
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%" }} direction="vertical">
              <Button
                type="primary"
                htmlType="submit"
                loading={copyLoading}
                block
              >
                Sao chép & Đăng ngay
              </Button>
              <Button onClick={handleCopyAndEdit} block icon={<CopyOutlined />}>
                Sao chép & Chỉnh sửa
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PageDetails;
